import { useState } from 'react';
import {
	PencilIcon,
	CheckIcon,
	XMarkIcon,
	ChevronDownIcon,
	ChevronRightIcon,
} from '@heroicons/react/24/outline';
import useAsync from '../../hooks/useAsync';
import {
	getManufacturers,
	getModels,
	updateManufacturer,
	updateModel,
} from '../../api/assets';

const EditableItem = ({ item, onSave, isSubmitting, isModel = false }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(item.name);

	const handleSave = async () => {
		if (editValue.trim() && editValue !== item.name) {
			await onSave(item.id, editValue.trim());
		}
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditValue(item.name);
		setIsEditing(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleSave();
		} else if (e.key === 'Escape') {
			handleCancel();
		}
	};

	return (
		<div
			className={`flex items-center justify-between group py-3 ${isModel ? 'pl-8' : ''}`}
		>
			{isEditing ? (
				<div className="flex items-center gap-2 flex-1">
					<input
						type="text"
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						onKeyDown={handleKeyDown}
						className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
						autoFocus
						disabled={isSubmitting}
					/>
					<button
						type="button"
						onClick={handleSave}
						disabled={isSubmitting || !editValue.trim()}
						className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
					>
						<CheckIcon className="h-5 w-5" />
					</button>
					<button
						type="button"
						onClick={handleCancel}
						disabled={isSubmitting}
						className="p-1 text-gray-400 hover:text-gray-600"
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>
			) : (
				<>
					<span
						className={`text-sm ${isModel ? 'text-gray-600' : 'text-gray-900 font-medium'}`}
					>
						{item.name}
					</span>
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="p-1 text-gray-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
					>
						<PencilIcon className="h-4 w-4" />
					</button>
				</>
			)}
		</div>
	);
};

const ManufacturerGroup = ({
	manufacturer,
	models,
	onUpdateManufacturer,
	onUpdateModel,
	isSubmitting,
}) => {
	const [isExpanded, setIsExpanded] = useState(true);

	const manufacturerModels = models.filter(
		(model) =>
			model.manufacturer?.id === manufacturer.id ||
			model.manufacturer_id === manufacturer.id,
	);

	return (
		<div className="border-b border-gray-200 last:border-b-0">
			<div className="flex items-center hover:bg-gray-50 px-4">
				<button
					type="button"
					onClick={() => setIsExpanded(!isExpanded)}
					className="p-1 mr-2 text-gray-400 hover:text-gray-600"
				>
					{isExpanded ? (
						<ChevronDownIcon className="h-4 w-4" />
					) : (
						<ChevronRightIcon className="h-4 w-4" />
					)}
				</button>
				<div className="flex-1">
					<EditableItem
						item={manufacturer}
						onSave={onUpdateManufacturer}
						isSubmitting={isSubmitting}
					/>
				</div>
				<span className="text-xs text-gray-400 mr-2">
					{manufacturerModels.length} model
					{manufacturerModels.length !== 1 ? 's' : ''}
				</span>
			</div>
			{isExpanded && manufacturerModels.length > 0 && (
				<div className="bg-gray-50 border-t border-gray-100">
					{manufacturerModels
						.sort((a, b) =>
							(a.name || '')
								.toLowerCase()
								.localeCompare((b.name || '').toLowerCase()),
						)
						.map((model) => (
							<div
								key={model.id}
								className="px-4 border-b border-gray-100 last:border-b-0"
							>
								<EditableItem
									item={model}
									onSave={onUpdateModel}
									isSubmitting={isSubmitting}
									isModel
								/>
							</div>
						))}
				</div>
			)}
		</div>
	);
};

const AssetSettings = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { value: manufacturers, execute: refreshManufacturers } = useAsync(
		getManufacturers,
		{ immediate: true },
	);

	const { value: models, execute: refreshModels } = useAsync(getModels, {
		immediate: true,
	});

	const { execute: onUpdateManufacturer } = useAsync(updateManufacturer, {
		immediate: false,
	});

	const { execute: onUpdateModel } = useAsync(updateModel, {
		immediate: false,
	});

	const handleUpdateManufacturer = async (id, name) => {
		setIsSubmitting(true);
		try {
			await onUpdateManufacturer({ id, name });
			await refreshManufacturers();
		} catch (error) {
			console.error('Failed to update manufacturer:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleUpdateModel = async (id, name) => {
		setIsSubmitting(true);
		try {
			await onUpdateModel({ id, name });
			await refreshModels();
		} catch (error) {
			console.error('Failed to update model:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Sort manufacturers alphabetically
	const sortedManufacturers = [...(manufacturers?.rows || [])].sort((a, b) =>
		(a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()),
	);

	const allModels = models?.rows || [];

	return (
		<div>
			<h2 className="text-base font-semibold leading-6 text-gray-900">
				Asset Settings
			</h2>
			<p className="mt-1 text-sm text-gray-500 mb-6">
				Manage manufacturers and models used across your assets. Click on a name
				to edit it.
			</p>

			<div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg mt-6">
				<div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
					<h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
						Manufacturers & Models
					</h3>
				</div>
				<div className="bg-white divide-y divide-gray-200">
					{sortedManufacturers.length === 0 ? (
						<div className="px-4 py-4 text-sm text-gray-500">
							No manufacturers found
						</div>
					) : (
						sortedManufacturers.map((manufacturer) => (
							<ManufacturerGroup
								key={manufacturer.id}
								manufacturer={manufacturer}
								models={allModels}
								onUpdateManufacturer={handleUpdateManufacturer}
								onUpdateModel={handleUpdateModel}
								isSubmitting={isSubmitting}
							/>
						))
					)}
				</div>
			</div>
		</div>
	);
};

export default AssetSettings;
